import * as Yup from 'yup';
import 'yup-phone';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/system';
import { Typography, Button, Dialog, DialogTitle, DialogContent } from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormInput, ModalTitle } from 'components';
import { isHipaaLink } from 'config/productProfile';
import { TwoFactor } from 'types';
import { withOutsideClickPreventer } from 'utils';

import styles from './SendCodePopup.module.scss';

type SendCodePopupProps = {
  open: boolean;
  onSubmit: (phone: string, twoFactor: TwoFactor) => void;
  onClose: () => void;
};

export const SendCodePopup = ({ open, onSubmit, onClose }: SendCodePopupProps) => {
  const { t } = useTranslation('settings');
  const phoneSchema = useMemo(
    () =>
      Yup.object().shape({
        phone: Yup.string().phone('', false, t('securityPopups.mustBeValidPhoneNumber')),
      }),
    [t],
  );

  const phoneForm = useForm<{ phone: string }>({
    resolver: yupResolver(phoneSchema),
    defaultValues: {},
    mode: 'all',
  });

  const handleCall = () => {
    if (phoneForm.formState.isValid) {
      const phone: string = phoneForm.getValues().phone;
      onSubmit(phone, 'call');
    }
  };

  const handleText = () => {
    if (phoneForm.formState.isValid) {
      const phone: string = phoneForm.getValues().phone;
      onSubmit(phone, 'sms');
    }
  };

  return (
    <Dialog open={open} onClose={withOutsideClickPreventer(onClose)} className={styles.dialog}>
      <DialogTitle>
        <ModalTitle title={t('securityPopups.setup2faTitle')} onClose={onClose} />
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        <Typography variant='body2'>
          {isHipaaLink ? t('securityPopups.hipaaSecondAuthDescription') : t('securityPopups.via3SecondAuthDescription')}
        </Typography>

        <FormInput
          placeholder={t('securityPopups.enterPhoneNumber')}
          name='phone'
          type='phone'
          prefix='+1'
          maxLength={13}
          control={phoneForm.control}
        />

        <Typography variant='body4'>{t('securityPopups.selectSecurityCodeDelivery')}</Typography>

        <Box className={styles.buttonsContaine}>
          <Button disabled={!phoneForm.formState.isValid} variant='contained' onClick={handleCall}>
            {t('securityPopups.call')}
          </Button>

          <Button disabled={!phoneForm.formState.isValid} variant='contained' onClick={handleText}>
            {t('securityPopups.text')}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
